import { useState } from 'react';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';
import crypto from 'crypto';

import { getMembership, refreshUserRoles } from '../../../api/profile';
import { COOKIE, LOCAL_STORAGE } from '../../../constants';
import { TidalButton } from '../../common/button';
import { TidalTag } from '../../common/tag';
import ShoppingBag from '../../svgs/ShoppingBag';
import Thumb from '../../svgs/Thumb';
import Ticket from '../../svgs/Ticket';
import Wait from '../../svgs/Wait';
import Refresh from '../../svgs/Refresh';
import { TailSpin } from 'react-loader-spinner';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { analytics } from '../../../lib/analytics';

interface ITidalDashboardProfileCardTag {
  count: number;
  info: string;
  Icon: any;
  hasBorder: boolean;
  isCenter: boolean;
  backgroundColor: string;
}

const TidalDashboardProfileCardTagWrapper = styled.div<{
  hasBorder: boolean;
  isCenter: boolean;
  backgroundColor: string;
}>`
  display: grid;
  grid-template-columns: 27px auto;
  grid-gap: 0 8px;
  padding-right: 4px;
  justify-content: ${(props) => (props.isCenter ? 'center' : 'flex-start')};
  @media only screen and (max-width: 370px) {
    justify-content: flex-start;
  }
  border-right: ${(props) =>
    props.hasBorder ? `1px solid ${props.theme.colors.borderColors[0]}` : '0'};

  & > .icon-wrapper {
    height: 27px;
    width: 100%;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${(props) => props.backgroundColor};
  }
  & > div > .count {
    font-weight: 600;
    font-size: 14px;
    line-height: 12px;
    color: ${(props) => props.theme.colors.text[1]};
  }
  & > div > .info {
    font-weight: 600;
    font-size: 12px;
    color: ${(props) => props.theme.colors.text[0]};
  }
`;

const TidalDashboardProfileCardTag = ({
  count,
  info,
  Icon,
  hasBorder,
  isCenter,
  backgroundColor,
}: ITidalDashboardProfileCardTag) => {
  return (
    <TidalDashboardProfileCardTagWrapper
      hasBorder={hasBorder}
      isCenter={isCenter}
      backgroundColor={backgroundColor}
    >
      <div className="icon-wrapper">
        <Icon />
      </div>
      <div>
        <p className="count">{count}</p>
        <p className="info">{info}</p>
      </div>
    </TidalDashboardProfileCardTagWrapper>
  );
};

const TidalDashboardProfileCardWrapper = styled.div`
  width: 100%;
  margin-bottom: 32px;
  border: 1px solid ${(props) => props.theme.colors.borderColors[0]};
  border-radius: 12px;
  padding: 28px 32px;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  box-shadow: 0px 14px 29px rgba(0, 27, 31, 0.04);

  @media only screen and (max-width: 930px) {
    grid-template-columns: 1fr;
  }
  @media only screen and (max-width: 800px) {
    box-shadow: 0 0 0;
    padding: 16px;
  }
`;

const TidalDashboardProfileCardLeft = styled.div`
  border-right: 1px solid ${(props) => props.theme.colors.borderColors[1]};
  padding-right: 23px;
  @media only screen and (max-width: 930px) {
    padding-right: 0;
    border-right: 0;
  }
  & > .header {
    display: grid;
    grid-template-columns: 85px 1fr;
    grid-gap: 0 19px;
    img {
      border-radius: 20px;
      height: 85px;
      width: 85px;
    }
    @media only screen and (max-width: 406px) {
      & > div:nth-child(2) {
        margin-left: 0px;
        margin-top: 8px;
      }
    }
    @media only screen and (max-width: 370px) {
      grid-template-columns: 1fr;
    }
    & > div > .user-name {
      font-weight: 700;
      font-size: 18px;
      line-height: 22px;
      color: ${(props) => props.theme.colors.text[1]};
      span {
        color: ${(props) => props.theme.colors.text[0]};
      }
    }
    & > div > .member-since {
      margin-top: 5px;
      font-weight: 600;
      font-size: 12px;
      color: ${(props) => props.theme.colors.text[0]};
    }
  }
  & > .empty-body {
    margin: 17px 0;
    padding: 16px 0;
    border-top: 1px solid ${(props) => props.theme.colors.borderColors[1]};
    border-bottom: 1px solid ${(props) => props.theme.colors.borderColors[1]};
  }
  & > .body {
    margin: 17px 0;
    padding: 16px 0;
    border-top: 1px solid ${(props) => props.theme.colors.borderColors[1]};
    border-bottom: 1px solid ${(props) => props.theme.colors.borderColors[1]};
    display: grid;
    grid-template-columns: 1fr 2fr;
    justify-content: space-between;
    & > div > .label {
      font-weight: 600;
      font-size: 12px;
      color: ${(props) => props.theme.colors.text[0]};
    }
    & > div > .value {
      font-weight: 600;
      font-size: 12px;
      color: ${(props) => props.theme.colors.text[1]};
    }
    & > div > .info {
      font-weight: 600;
      font-size: 12px;
      line-height: 15px;
      color: ${(props) => props.theme.colors.text[1]};
    }
  }
  & > .footer {
  }
`;

const TidalDashboardProfileProgressBar = styled.div<{
  progress: number;
}>`
  width: 100%;
  .progress-bar-container {
    width: 100%;
    position: relative;
    background: #f2f4f9;
    border-radius: 40px;
    height: 7px;
    margin-top: 8px;
  }
  .progress-bar {
    height: 7px;
    position: absolute;
    background: #1cbaf0;
    border-radius: 40px;
    width: ${(props) => props.progress}%;
  }
`;

const TidalDashboardProfileCardRight = styled.div`
  padding-left: 19px;
  @media only screen and (max-width: 930px) {
    padding-left: 0;
    margin-top: 24px;
  }
  & > .header {
    border-bottom: 1px solid ${(props) => props.theme.colors.borderColors[1]};
    padding-bottom: 11px;
    & > .rep-wrapper {
      display: grid;
      grid-template-columns: 45px 1fr;
      grid-gap: 0 13px;
      & > .thumb-wrapper {
        height: 45px;
        width: 45px;
        border-radius: 10px;
        background-color: ${(props) => props.theme.colors.accent};
        display: flex;
        align-items: center;
        justify-content: center;
      }
      & > div {
        & > .rep-count {
          font-weight: 700;
          color: ${(props) => props.theme.colors.text[1]};
        }
        & > .rep-description {
          font-weight: 600;
          font-size: 14px;
          color: ${(props) => props.theme.colors.accent};
        }
      }
    }
    & > .profile-stats {
      display: grid;
      margin-top: 14px;
      grid-template-columns: 1fr 1fr 1fr;
      grid-gap: 4px;
      @media only screen and (max-width: 406px) {
        display: flex;
        grid-gap: 0;
      }
      @media only screen and (max-width: 370px) {
        display: block;
      }
    }
  }
  & > .roles-container {
    margin-top: 14px;
    & > .roles-heading-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      & > .refresh-row {
        display: flex;
        align-items: center;
        grid-gap: 6px;
        p {
          font-size: 10px;
        }
      }
    }

    & > .heading {
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      margin-bottom: 10px;
      color: ${(props) => props.theme.colors.text[1]};
    }
    & > .roles-grid {
      display: flex;
      grid-gap: 8px;
      flex-wrap: wrap;
      overflow-y: auto;
      max-height: 90px;
    }
  }
`;

interface ITidalDashboardProfileCard {
  name: string;
  tag: string;
  memberSince: string | null;
  image: string;
  reputation: string;
  isSubscriptionActive: boolean;
  discordConnection: string;
  subscriptionEndDate?: string;
  subscriptionStartDate?: string;
  userRoles: any[];
  onRolesRefreshed: () => void;
  all: {
    bought: number;
    sold: number;
    total: number;
    rented: number;
  };
}

const TidalDashboardProfileCard = ({
  name,
  tag,
  memberSince,
  image,
  reputation,
  isSubscriptionActive,
  all,
  onRolesRefreshed,
  discordConnection,
  subscriptionEndDate,
  subscriptionStartDate,
  userRoles,
}: ITidalDashboardProfileCard): JSX.Element => {
  const [cookies, _, removeCookie] = useCookies([COOKIE.ACCESS_TOKEN]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshingRoles, setIsRefreshingRoles] = useState(false);

  const handleGetMembership = async () => {
    setIsLoading(true);
    try {
      const res = await getMembership(discordConnection);
      analytics.track('User Clicked Subscription Button', {
        discordConnection,
      });
      window.location.href = res.data.url;
    } catch (e) {}
    setIsLoading(false);
  };

  const handleRefreshToken = async () => {
    setIsRefreshingRoles(true);
    try {
      await refreshUserRoles(discordConnection);
      analytics.track('User Refreshed Tidal Roles', {
        discordConnection,
      });
      onRolesRefreshed();
    } catch (e) {
      toast(e.message, {
        type: 'error',
      });
    }
    setIsRefreshingRoles(false);
  };

  const getDaysRemaining = () => {
    const endDate = dayjs(new Date(subscriptionEndDate));
    const now = dayjs(new Date());
    const start = dayjs(new Date(subscriptionStartDate));

    const difference = parseInt(
      endDate.startOf('day').diff(start.startOf('day'), 'days').toFixed(),
    );

    const daysRemaining = parseInt(endDate.diff(now, 'days').toFixed());

    if (daysRemaining > 0) {
      const percentageOfRemainingDays = (daysRemaining / difference) * 100;
      return {
        totalDays: difference,
        daysRemaining,
        percentageOfRemainingDays,
      };
    } else {
      return {
        totalDays: 0,
        daysRemaining: 0,
        percentageOfRemainingDays: 0,
      };
    }
  };

  const onRedirectToDiscord = () => {
    const state = crypto.randomBytes(10).toString('hex');
    localStorage.setItem(LOCAL_STORAGE.DISCORD_OAUTH_STATE, state);
    localStorage.setItem(
      LOCAL_STORAGE.REDIRECT_AFTER_AUTH,
      '/tidal-dashboard/profile',
    );
    analytics.track('User Started Discord Connect');

    window.location.assign(
      `${process.env.DISCORD_OAUTH_URL_TEMPLATE.split('%code%')
        .join(
          encodeURIComponent(
            `${process.env.NEXT_PUBLIC_BASE_URL}/register/connect-discord`,
          ),
        )
        .split('%client_id%')
        .join(process.env.DISCORD_CLIENT_ID)}&state=${state}`,
    );
  };

  return (
    <TidalDashboardProfileCardWrapper>
      <TidalDashboardProfileCardLeft>
        <div className="header">
          <div>{name && <img alt="" src={image} />}</div>
          <div>
            {name ? (
              <p className="user-name">
                {name}#<span>{tag}</span>
              </p>
            ) : (
              <p className="user-name"></p>
            )}
            {name && memberSince && (
              <p className="member-since">
                Member since: {new Date(memberSince).toDateString()}
              </p>
            )}
          </div>
        </div>
        {isSubscriptionActive ? (
          <div className="body">
            <div>
              <p className="label">Expires</p>
              <p className="value">
                {new Date(subscriptionEndDate).toDateString()}
              </p>
            </div>
            <div>
              <p className="info">
                {getDaysRemaining().daysRemaining} days remaining
              </p>
              <TidalDashboardProfileProgressBar
                progress={getDaysRemaining().percentageOfRemainingDays}
              >
                <div className="progress-bar-container">
                  <div className="progress-bar" />
                </div>
              </TidalDashboardProfileProgressBar>
            </div>
          </div>
        ) : (
          <div className="empty-body">
            <p>No membership</p>
          </div>
        )}
        {name ? (
          <>
            <div className="footer">
              {isSubscriptionActive ? (
                <TidalButton
                  onClick={handleGetMembership}
                  type="info"
                  tint={false}
                  isLoading={isLoading}
                  text="Manage Membership"
                />
              ) : (
                <TidalButton
                  onClick={handleGetMembership}
                  type="info"
                  tint={false}
                  isLoading={isLoading}
                  text="Purchase Membership"
                />
              )}
            </div>
          </>
        ) : (
          <TidalButton
            onClick={onRedirectToDiscord}
            type="info"
            tint={false}
            isLoading={isLoading}
            text="Connect Discord"
          />
        )}
      </TidalDashboardProfileCardLeft>
      <TidalDashboardProfileCardRight>
        <div className="header">
          <div className="rep-wrapper">
            <div className="thumb-wrapper">
              <Thumb />
            </div>
            <div>
              <p className="rep-count">{reputation}</p>
              <p className="rep-description">Total Rep</p>
            </div>
          </div>
          <div className="profile-stats">
            <TidalDashboardProfileCardTag
              count={all?.sold}
              info="Sold"
              Icon={ShoppingBag}
              backgroundColor="#23E588"
              hasBorder={true}
              isCenter={false}
            />
            <TidalDashboardProfileCardTag
              count={all?.bought}
              info="Bought"
              Icon={Ticket}
              backgroundColor="#6046FF"
              hasBorder={true}
              isCenter={true}
            />
            <TidalDashboardProfileCardTag
              count={all?.rented}
              info="Rented"
              Icon={Wait}
              backgroundColor="#F3C05C"
              hasBorder={false}
              isCenter={true}
            />
          </div>
        </div>
        <div className="roles-container">
          <div className="roles-heading-row">
            <p className="heading">Roles</p>
            {isRefreshingRoles ? (
              <div>
                <TailSpin color="#0000ff" height={16} width={16} />
              </div>
            ) : (
              <button className="refresh-row" onClick={handleRefreshToken}>
                <Refresh />
                <p>REFRESH</p>
              </button>
            )}
          </div>
          <div className="roles-grid">
            {userRoles?.map((role, key) => (
              <div key={key}>
                <TidalTag title={role.name} color={role.color} />
              </div>
            ))}
          </div>
        </div>
      </TidalDashboardProfileCardRight>
    </TidalDashboardProfileCardWrapper>
  );
};

export default TidalDashboardProfileCard;
